import { computed, defineComponent, onMounted, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { ActivityAction, ActivityGetter } from '@/store/activity/activity.module';
import { Activity } from '@/store/activity/activity.model';
import { ActivitySchema } from '@/store/activity/activity.validator';
import { ProvinceAction, ProvinceGetter } from '@/store/province/province.module';
import { Province } from '@/store/province/province.model';
import Datepicker from '@vuepic/vue-datepicker';
import Multiselect from '@vueform/multiselect';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'ActivityFormComponent',
  components: {
    Datepicker,
    Multiselect,
    BaseModalComponent,
  },
  props: {
    startDate: {
      type: Date,
    },
    endDate: {
      type: Date,
    },
    activityId: {
      type: Number,
    }
  },
  setup: (props) => {
    const store = useStore();
    const isEditMode = computed(() => props.activityId != null);
    const title = computed(() => isEditMode.value ? 'แก้ไขข้อมูลการลงพื้นที่ตรวจราชการ' : 'เพิ่มข้อมูลการลงพื้นที่ตรวจราชการ');
    const activity = computed<Activity | undefined>(() => store.getters[ActivityGetter.BY_ID](props.activityId));
    const provinces = computed<Province[]>(() => {
      const all = store.getters[ProvinceGetter.ALL] as Province[];
      return all.filter(m => m.iso !== 'TH-10');
    });
    const provinceOptions = computed<string[]>(() => provinces.value.map(p => p.name));
    const activityForm = ref<InstanceType<typeof BaseModalComponent>>();

    // form
    interface Form {
      id?: number | null;
      name?: string;
      description?: string;
      startDate?: Date | null;
      endDate?: Date | null;
      provinces?: string[] | null;
      hasMessage?: boolean;
    }
    const formData = reactive<Form>({
      id: null,
      name: '',
      description: '',
      startDate: null,
      endDate: null,
      provinces: [],
      hasMessage: false,
    });
    const validator = computed(() => ActivitySchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      name: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'name')),
      startDate: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'startDate')),
      endDate: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'endDate')),
      provinces: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'provinces')),
    });
    const isValid = computed(() => !errors.name && !errors.startDate);
    const isLoading = computed<boolean>(() => store.getters[ActivityGetter.STATUS] === 'init' || store.getters[ActivityGetter.STATUS] === 'loading');
    const submited = ref(false);

    onMounted(() => {
      store.dispatch(ProvinceAction.FETCH);
    });

    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        formData.startDate = props.startDate;
        formData.endDate = (props.startDate?.getTime() !== props.endDate?.getTime()) ? props.endDate : null;
        if (isEditMode.value) {
          formData.id = props.activityId;
          formData.name = activity.value?.name;
          formData.description = activity.value?.description;
          formData.startDate = activity.value?.startDate;
          formData.endDate = activity.value?.endDate;
          formData.provinces = activity.value?.provinces.map(m => m.name);
          formData.hasMessage = activity.value?.hasMessage;
        }
        resolve();
      })
      .then(() => activityForm.value?.show());
    };

    // hide modal
    const hide = () => {
      activityForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.id = null;
        formData.name = '';
        formData.description = '';
        formData.endDate = null;
        formData.provinces = [];
        formData.hasMessage = false;
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        if (isEditMode.value) {
          store.dispatch(ActivityAction.UPDATE, formData)
            .then(() => hide());
        } else {
          store.dispatch(ActivityAction.CREATE, formData)
            .then(() => hide());
        }
      }
    };

    return {
      title,
      formData,
      isEditMode,
      errors,
      submited,
      isLoading,
      activityForm,
      provinceOptions,
      show,
      hide,
      submitClick,
      unsubmit,
      clearForm,
    };
  }
});
